import axios from 'axios';
import STATUS_CODES from 'constants/statusCodes';
import JsonApiDeserializer from 'jsona';
import qs from 'qs';
import { isServerSide } from 'helpers/isServerSide';
import { notifyNetworkError } from '../../helpers/errorNotifier';
import { addAPIV3HeadersInterceptor, addAuthInterceptor } from './interceptors/addAuthInterceptor';
import { addGoogleAnalyticsClientIdInterceptor } from './interceptors/addGoogleAnalyticsInterceptor';
import { setupRedirectInterceptor } from './interceptors/redirectInterceptor';
import { requestDuration, requestStartTime } from './interceptors/requestTime';
import { addUserInfoForRequestTracker } from './interceptors/addReblazeUserId';
import { getRailsBaseUrl, publicEnvConfig } from 'helpers/env';
import { setupAdminRedirectToBosssiteLoginInterceptor } from './interceptors/adminRedirectToBosssiteLogin';
import { addScreenHeadersInterceptor } from './interceptors/screenHeadersInterceptor';
import { PUT, POST, GET, DELETE, PATCH } from './helpers/restMethods';
import { ignoreErrorsForCertainEndpoints } from './helpers/ignoreErrors';

export const formatResponse = (data, serializationType) => {
	if (!data) return {};

	try {
		const parsedData = JSON.parse(data);
		let deserializedData;

		switch (serializationType) {
			case 'json:api':
				const formatter = new JsonApiDeserializer();
				deserializedData = {
					data: formatter.deserialize(parsedData),
					...(parsedData.meta && { meta: parsedData.meta }),
				};
				break;
			default:
				deserializedData = parsedData;
		}

		return deserializedData;
	} catch (e) {
		throw new Error(`Error formatting data using type "${serializationType}": ${e}. \nUnformatted data: ${data}`);
	}
};

export const defaultConfigurationParam = Object.freeze({
	withCredentials: true, // need it for utm tracking, not only for auth.
	// credentials: publicEnvConfig.RELEASE_STAGE !== 'production' ? 'include' : 'same-origin', **credentials is not a valid option for the axios config**
	withAutoAuthentication: false,
	sendAuthHeaders: false,
	externalRequest: false, // if making request to external APIS please turn this to true
});

const getDefaultBaseUrl = () => (isServerSide() ? getRailsBaseUrl() : publicEnvConfig.RELATIVE_BASE_URL);

export class RestClient {
	constructor(config = defaultConfigurationParam, serializationType = 'rest') {
		const defaultBaseUrl = getDefaultBaseUrl();

		this.config = config;
		this._serializationType = serializationType;
		this._baseUrl = config.baseUrl || defaultBaseUrl;

		if (!publicEnvConfig.NEXT_PUBLIC_NO_AXIOS_TIMEOUT) {
			this._timeout = config.timeout || 30000;
		}

		const defaultConfig = {
			baseURL: this._baseUrl,
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				Application: 'webapp',
			},
			timeout: this._timeout,
			transformResponse: (data) => formatResponse(data, serializationType),
		};

		const credentialsConfig = {
			...(config.withCredentials && { withCredentials: config.withCredentials }),
			...(config.credentials && { credentials: config.credentials }),
		};

		const axiosConfig = { ...defaultConfig, ...credentialsConfig };

		this._axios = axios.create(axiosConfig);
		addAuthInterceptor(config, this);
		addUserInfoForRequestTracker(config, this);
		addAPIV3HeadersInterceptor(config, this);
		addGoogleAnalyticsClientIdInterceptor(config, this);
		setupAdminRedirectToBosssiteLoginInterceptor(config, this);
		addScreenHeadersInterceptor(config, this);
		this._axios.interceptors.request.use(requestStartTime);
		this._axios.interceptors.response.use(requestDuration);
		setupRedirectInterceptor(this);
	}

	get(url, params = {}, headers = {}, cancelToken = null) {
		return this._get(url, params, headers, cancelToken);
	}

	_get(url, params = {}, headers = {}, cancelToken = null) {
		const payload = {
			url: url,
			method: GET,
			headers: headers,
			params: params,
			paramsSerializer: (params) => qs.stringify(params),
			...(cancelToken && { cancelToken }),
		};
		return this.makeRequest(payload);
	}

	delete(url, headers = {}, data = {}, cancelToken = null) {
		return this.ajax(url, data, headers, DELETE, cancelToken);
	}

	post(url, data, headers = {}, cancelToken = null) {
		return this.ajax(url, data, headers, POST, cancelToken);
	}

	put(url, data, headers = {}, cancelToken = null) {
		return this.ajax(url, data, headers, PUT, cancelToken);
	}

	patch(url, data, headers = {}, cancelToken = null) {
		return this.ajax(url, data, headers, PATCH, cancelToken);
	}

	ajax(url, data = {}, headers = {}, method, cancelToken) {
		return this.makeRequest({
			url: url,
			baseURL: this._baseUrl,
			headers: headers,
			method: method,
			data: data,
			...(cancelToken && { cancelToken }),
		});
	}

	async makeRequest(requestPayload) {
		try {
			const response = await this._axios.request(requestPayload);
			return {
				status: response.status,
				data: response.data,
				headers: response.headers,
			};
		} catch (error) {
			const errorResponse = this.buildErrorResponse(error);
			this.reportError(errorResponse, requestPayload?.url, requestPayload?.method);
			throw errorResponse;
		}
	}

	setUserToErrorLog() {
		const { user = {} } = this.config;
		const { email, id } = user;
		return (event) => event.setUser(id, email);
	}

	reportError(error, url, method) {
		// 401s are intentionally returned by us, so they are expected and we don't need to tell bugsnag
		const statusesToIgnore = [STATUS_CODES.UNAUTHORIZED, STATUS_CODES.FORBIDDEN];

		if (
			error?.isCancel ||
			statusesToIgnore.includes(error.status) ||
			ignoreErrorsForCertainEndpoints(error, url, method)
		) {
			return;
		}

		notifyNetworkError(
			error,
			{
				endpoint: url,
				method,
				status: error?.status,
			},
			this.setUserToErrorLog(),
		);
	}

	buildErrorResponse(error) {
		const defaultStatus = STATUS_CODES.INTERNAL_SERVER_ERROR;
		const result = {};
		result.isCancel = axios.isCancel(error);

		if (error.response) {
			/*
			 * The request was made and the server responded with a
			 * status code that falls out of the range of 2xx
			 */
			result.error = error.response.data || error.response.message;
			result.status = error.response.status;
		} else if (error.request) {
			/*
			 * The request was made but no response was received, `error.request`
			 * is an instance of XMLHttpRequest in the browser and an instance
			 * of http.ClientRequest in Node.js
			 */
			result.status = error.request.status;
			result.error = error.request;
		} else {
			// Something happened in setting up the request and triggered an Error
			result.status = defaultStatus;
			result.error = error.message;
		}

		result.status = result.status || defaultStatus;

		return result;
	}

	getCancelToken() {
		return axios.CancelToken.source();
	}
}

// here we export the most commons configuration
// the idea is to use same instance for same configuration so please avoid to create RestClient instances if not needed
export const authenticatedRestClient = new RestClient({
	...defaultConfigurationParam,
	withAutoAuthentication: true,
	withCredentials: true,
	sendAuthHeaders: true,
	sendAPIV3Headers: true,
	trackScreenSize: true,
});

/**
 * This instance will not send the Authorization header.
 */
export const withCookiesRestClient = new RestClient({
	...defaultConfigurationParam,
	withAutoAuthentication: true,
	withCredentials: true,
	credentials: publicEnvConfig.RELEASE_STAGE === 'development' ? 'include' : 'same-origin',
	sendAPIV3Headers: true,
	trackScreenSize: true,
});

export const bossiteUsersRestClient = new RestClient({
	...defaultConfigurationParam,
	withAutoAuthentication: true,
	withCredentials: true,
	credentials: true,
	sendAPIV3Headers: true,
	bossiteAdminUser: true,
	trackScreenSize: true,
});
